if (!customElements.get('shark-sales-popups')) {
  customElements.define('shark-sales-popups', class extends HTMLElement {
    connectedCallback() {
      this.crosssell = this.querySelector('[data-crosssell-dialog]');
      this.campaign = this.querySelector('[data-campaign-dialog]');
      this.seen = new Set();
      this.requestNumber = 0;
      this.onAdded = (event) => { this.loadAccessories(event.detail); };
      this.onConsent = () => this.scheduleCampaign();
      this.onVisibility = () => { if (!document.hidden) this.scheduleCampaign(); };
      this.onBlockSelect = (event) => {
        if (this.dataset.designMode !== 'true') return;
        const template = [...this.querySelectorAll('[data-shark-campaign]')].find((node) => node.dataset.blockId === event.detail.blockId);
        if (template) this.showCampaign(template, true);
      };
      this.onClick = (event) => {
        if (event.target.closest('[data-popup-close]')) this.closeDialog();
        else if (event.target.closest('[data-popup-cart]')) this.viewCart();
        else if (event.target.closest('[data-copy-code]')) this.copyCode(event.target.closest('[data-copy-code]'));
        else if (event.target.closest('[data-campaign-preview]') && this.dataset.designMode === 'true') {
          const id = event.target.closest('[data-campaign-preview]').dataset.campaignPreview;
          const template = [...this.querySelectorAll('[data-shark-campaign]')].find((node) => node.dataset.blockId === id);
          if (template) this.showCampaign(template, true);
        }
      };
      this.onSubmit = (event) => {
        if (!event.target.matches('[data-shark-accessory-form]')) return;
        event.preventDefault();
        this.addAccessory(event.target);
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      for (const dialog of [this.crosssell, this.campaign]) {
        dialog.addEventListener('click', (event) => { if (event.target === dialog) this.closeDialog(); });
        dialog.addEventListener('close', () => {
          if (this.currentDialog === dialog) this.currentDialog = null;
          if (!this.querySelector('dialog[open]')) document.body.classList.remove('shark-sales-open');
        });
      }
      document.addEventListener('shark:cart-added', this.onAdded);
      document.addEventListener('visitorConsentCollected', this.onConsent);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'product-form') this.loadAccessories(event);
        });
      }
      if (this.dataset.designMode !== 'true') this.scheduleCampaign();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.abort?.abort();
      clearTimeout(this.campaignTimer);
      clearTimeout(this.fetchTimer);
      document.removeEventListener('shark:cart-added', this.onAdded);
      document.removeEventListener('visitorConsentCollected', this.onConsent);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('submit', this.onSubmit);
      this.closeDialog();
    }

    loadStyle() {
      if (this.stylePromise) return this.stylePromise;
      this.stylePromise = new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = this.dataset.styleUrl;
        link.onload = resolve;
        link.onerror = () => reject(new Error('popup_style_unavailable'));
        document.head.append(link);
      });
      return this.stylePromise;
    }

    dateAllowed(template) {
      const parts = new Intl.DateTimeFormat('en', {timeZone: 'Europe/Zurich', year: 'numeric', month: '2-digit', day: '2-digit'}).formatToParts(new Date());
      const part = (name) => parts.find((item) => item.type === name).value;
      const today = `${part('year')}-${part('month')}-${part('day')}`;
      const start = template.dataset.startDate.trim();
      const end = template.dataset.endDate.trim();
      const validDate = (value) => !value || /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
      return validDate(start) && validDate(end) && (!start || start <= today) && (!end || end >= today) && (!start || !end || start <= end);
    }

    campaignKey(template) {
      return `shark-campaign:${template.dataset.blockId}:${template.dataset.code}:${template.dataset.startDate}:${template.dataset.endDate}`;
    }

    wasSeen(key) {
      if (this.seen.has(key)) return true;
      try { return sessionStorage.getItem(key) === 'shown'; } catch (error) { return false; }
    }

    markSeen(key) {
      this.seen.add(key);
      try { sessionStorage.setItem(key, 'shown'); } catch (error) { return; }
    }

    overlayVisible(node) {
      if (this.contains(node) || !node.getClientRects().length
          || node.closest('[hidden], [aria-hidden="true"], [inert]')) return false;
      for (let element = node; element; element = element.parentElement) {
        const style = getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden'
            || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
      }
      const rect = node.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0
        && rect.top < window.innerHeight && rect.left < window.innerWidth;
    }

    blockingOverlay() {
      if (document.hidden || document.querySelector('cart-drawer.active')) return true;
      const consentRoot = document.querySelector('pandectes-cmp[data-active-instance="true"]')?.shadowRoot;
      if (consentRoot) {
        if ([...consentRoot.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]')]
            .some((node) => this.overlayVisible(node))) return true;
      } else {
        try {
          if (window.Shopify?.customerPrivacy?.shouldShowBanner?.()) return true;
        } catch (error) { return true; }
      }
      return [...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]')]
        .some((node) => this.overlayVisible(node));
    }

    scheduleCampaign() {
      clearTimeout(this.campaignTimer);
      const template = [...this.querySelectorAll('[data-shark-campaign]')].find((node) =>
        node.dataset.enabled === 'true' && node.dataset.matchesProduct === 'true' && node.dataset.code.trim() && this.dateAllowed(node) && !this.wasSeen(this.campaignKey(node)));
      if (!template) return;
      let attempts = 0;
      const tryShow = () => {
        if (!this.isConnected || this.currentDialog || this.wasSeen(this.campaignKey(template))) return;
        if (this.blockingOverlay()) {
          if (++attempts < 30) this.campaignTimer = setTimeout(tryShow, 1000);
          return;
        }
        this.showCampaign(template, false);
      };
      this.campaignTimer = setTimeout(tryShow, 1800);
    }

    async showCampaign(template, editorPreview) {
      try { await this.loadStyle(); } catch (error) { return; }
      if (!this.isConnected || !editorPreview && (this.blockingOverlay() || !this.dateAllowed(template))) return;
      this.closeDialog();
      const target = this.querySelector('[data-campaign-content]');
      target.replaceChildren(template.content.cloneNode(true));
      const heading = target.querySelector('h2');
      this.campaign.setAttribute('aria-labelledby', heading.id);
      target.querySelector('[data-editor-note]').hidden = !editorPreview;
      this.showDialog(this.campaign);
      if (!editorPreview) this.markSeen(this.campaignKey(template));
    }

    showDialog(dialog) {
      if (typeof dialog.showModal !== 'function') return;
      const drawer = document.querySelector('cart-drawer.active');
      this.opener = drawer?.activeElement?.isConnected ? drawer.activeElement : document.activeElement;
      drawer?.close();
      dialog.showModal();
      this.currentDialog = dialog;
      document.body.classList.add('shark-sales-open');
      dialog.querySelector('button')?.focus({preventScroll: true});
    }

    closeDialog() {
      for (const dialog of [this.crosssell, this.campaign]) if (dialog?.open) dialog.close();
      this.currentDialog = null;
      document.body.classList.remove('shark-sales-open');
    }

    viewCart() {
      this.closeDialog();
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.open(this.opener);
      else window.location.assign(`${this.dataset.root}cart`);
    }

    async copyCode(button) {
      const input = this.campaign.querySelector('input[readonly]');
      const status = this.campaign.querySelector('[data-copy-status]');
      try {
        await navigator.clipboard.writeText(input.value);
        button.textContent = button.dataset.copiedLabel;
        status.textContent = button.dataset.copiedLabel;
      } catch (error) {
        input.focus(); input.select();
        let copied = false;
        try { copied = document.execCommand('copy'); } catch (failure) { copied = false; }
        if (copied) {
          button.textContent = button.dataset.copiedLabel;
          status.textContent = button.dataset.copiedLabel;
        } else status.textContent = status.dataset.fallback;
      }
    }

    async loadAccessories(event) {
      if (this.dataset.crosssellEnabled !== 'true' || !event?.cartData) return;
      clearTimeout(this.campaignTimer);
      this.abort?.abort();
      clearTimeout(this.fetchTimer);
      const items = event.cartData.items || [event.cartData];
      const added = items.find((item) => String(item.variant_id || item.id) === String(event.productVariantId)) || items.find((item) => !item.properties?._shark_montage_for);
      if (!added?.product_id || !added.url && !added.handle) return;
      const path = added.url || `/products/${added.handle}`;
      const productUrl = new URL(path, window.location.origin);
      const productPath = productUrl.pathname.match(/\/products\/[^/]+$/)?.[0];
      if (productUrl.origin !== window.location.origin || !productPath) return;
      const url = new URL(`${this.dataset.root.replace(/\/$/, '')}${productPath}`, window.location.origin);
      url.searchParams.set('section_id', 'shark-crosssell-products');
      const number = ++this.requestNumber;
      this.abort = new AbortController();
      this.fetchTimer = setTimeout(() => this.abort.abort(), 8000);
      try {
        const [response] = await Promise.all([fetch(url, {signal: this.abort.signal, credentials: 'same-origin'}), this.loadStyle()]);
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const recommendations = html.querySelector('[data-shark-accessories]');
        if (!this.isConnected || number !== this.requestNumber || recommendations?.dataset.productId !== String(added.product_id)) return;
        const max = Math.min(4, Math.max(1, Number(this.dataset.maxProducts) || 3));
        [...recommendations.children].slice(max).forEach((node) => node.remove());
        if (!recommendations.children.length) return;
        this.closeDialog();
        this.querySelector('[data-crosssell-error]').textContent = '';
        this.querySelector('[data-crosssell-products]').replaceChildren(recommendations);
        recommendations.style.setProperty('--shark-card-count', recommendations.children.length);
        this.showDialog(this.crosssell);
      } catch (error) { return; }
      finally { if (number === this.requestNumber) clearTimeout(this.fetchTimer); }
    }

    async addAccessory(form) {
      if (this.adding) return;
      this.adding = true;
      const buttons = [...this.crosssell.querySelectorAll('button[type="submit"]')];
      buttons.forEach((button) => { button.disabled = true; });
      const error = this.querySelector('[data-crosssell-error]');
      error.textContent = '';
      const drawer = document.querySelector('cart-drawer');
      const data = new FormData(form);
      if (drawer) {
        data.append('sections', drawer.getSectionsToRender().map((section) => section.id).join(','));
        data.append('sections_url', window.location.pathname);
      }
      let added = false;
      try {
        const response = await fetch(`${this.dataset.root}cart/add.js`, {method: 'POST', credentials: 'same-origin', headers: {Accept: 'application/json'}, body: data});
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || this.dataset.error);
        added = true;
        this.closeDialog();
        if (drawer && result.sections && Object.values(result.sections).every(Boolean)) {
          drawer.setActiveElement(this.opener);
          drawer.renderContents(result);
        } else window.location.assign(`${this.dataset.root}cart`);
      } catch (failure) {
        if (added) window.location.assign(`${this.dataset.root}cart`);
        else error.textContent = failure.message || this.dataset.error;
      } finally {
        this.adding = false;
        buttons.forEach((button) => { button.disabled = false; });
      }
    }
  });
}
